import { axiosInt } from '../utils/axios';
import { IFile } from '../types/file';

/**
 * Chunk upload way of working
 * 1. Generate upload link and fileId
 */

export interface IChunkUploaderOptions {
  chunkSize?: number; // Min size 5MB
  threadsQuantity?: number; // Number of parallel requests
  timeout?: number; // TODO: comment
  initializeUploadURL: string; // URL where to initialize upload
  finalizeUploadURL: string; // URL where to finalize upload
}

export interface IOnProgressOptions {
  sent: number;
  total: number;
  percentage: number;
}

export interface IPart {
  partNumber: number;
  signedUrl: string;
}

export interface IAWSPart {
  partNumber: number;
  eTag: string;
}

const MBToBytes = (MB: number) => {
  return 1024 * 1024 * MB;
};

class ChunkUploader {
  chunkSize: number;
  threadsQuantity: number;
  timeout: number;
  file?: File;
  aborted: boolean = false;
  uploadedSize: number = 0;
  progressCache: { [id: number]: number } = {};
  activeConnections: { [id: number]: XMLHttpRequest } = {};
  parts: IPart[] = [];
  uploadedParts: IAWSPart[] = [];
  fileId: string | null = null;
  filePath: string | null = null;
  initializeUploadURL: string;
  finalizeUploadURL: string;
  numberOfChunkToBeSend?: number;
  onProgressFn: (data: IOnProgressOptions) => void = () => {};
  onErrorFn: (error: any) => void = () => {};
  onCompleteFn: (data: IFile) => void = () => {};

  constructor(options: IChunkUploaderOptions) {
    this.chunkSize = Math.max(MBToBytes(options.chunkSize || 5), MBToBytes(5));
    this.threadsQuantity = Math.min(options.threadsQuantity || 1, 15);
    this.timeout = options.timeout || 0;
    this.initializeUploadURL = options.initializeUploadURL;
    this.finalizeUploadURL = options.finalizeUploadURL;
  }

  start(file: File) {
    this.file = file;

    this.initialize();
  }

  async initialize() {
    if (!this.file) {
      throw new Error('File is not set');
    }

    try {
      this.numberOfChunkToBeSend = Math.ceil(this.file.size / this.chunkSize);

      const initializeResponse = await axiosInt.request({
        url: this.initializeUploadURL,
        method: 'POST',
        data: {
          fileName: this.file.name,
          chunkSize: this.numberOfChunkToBeSend,
        },
      });

      this.fileId = initializeResponse.data.uploadId;
      this.filePath = initializeResponse.data.filePath;

      this.parts = initializeResponse.data.presignedUrls.map(
        (url: string, index: number) => ({
          partNumber: index + 1,
          signedUrl: url,
        })
      );

      this.sendNext();
    } catch (error) {
      await this.complete(error);
    }
  }

  sendNext(retry: number = 0) {
    const activeConnections = Object.keys(this.activeConnections).length;
    if (activeConnections >= this.threadsQuantity) {
      return;
    }

    if (!this.parts.length) {
      if (!activeConnections) {
        this.complete();
      }

      return;
    }

    const part = this.parts.pop();

    if (this.file && part) {
      const sentSize = (part.partNumber - 1) * this.chunkSize;
      const chunk = this.file.slice(sentSize, sentSize + this.chunkSize);

      const sendChunkStarted = () => {
        this.sendNext();
      };

      this.sendChunk(chunk, part, sendChunkStarted)
        .then(() => {
          this.sendNext();
        })
        .catch(error => {
          if (retry <= 6) {
            retry++;
            const wait = (ms: number) =>
              new Promise(res => setTimeout(res, ms));
            //exponential backoff retry before giving up
            console.log(
              `Part#${part.partNumber} failed to upload, backing off ${
                2 ** retry * 400
              } before retrying...`
            );
            wait(2 ** retry * 400).then(() => {
              this.parts.push(part);
              this.sendNext(retry);
            });
          } else {
            console.log(`Part#${part.partNumber} failed to upload, giving up`);
            this.parts.push(part); // Return back to array in case of retry
            this.complete(error);
          }
        });
    }
  }

  sendChunk(chunk: Blob, part: IPart, sendChunkStarted: () => void) {
    return new Promise<void>((resolve, reject) => {
      this.upload(chunk, part, sendChunkStarted)
        .then(status => {
          if (status !== 200) {
            reject(new Error('Failed chunk upload'));
            return;
          }

          resolve();
        })
        .catch((error: any) => {
          reject(error);
        });
    });
  }

  upload(file: Blob, part: IPart, sendChunkStarted: () => void) {
    // uploading each part with its pre-signed URL
    return new Promise((resolve, reject) => {
      const throwXHRError = (error: any, part: IPart, abortFx?: any) => {
        delete this.activeConnections[part.partNumber - 1];
        reject(error);
        window.removeEventListener('offline', abortFx);
      };
      if (this.fileId) {
        if (!window.navigator.onLine) reject(new Error('System is offline'));

        const xhr = (this.activeConnections[part.partNumber - 1] =
          new XMLHttpRequest());
        xhr.timeout = this.timeout;
        sendChunkStarted();

        const progressListener = this.handleProgress.bind(
          this,
          part.partNumber - 1
        );

        xhr.upload.addEventListener('progress', progressListener);

        xhr.addEventListener('error', progressListener);
        xhr.addEventListener('abort', progressListener);
        xhr.addEventListener('loadend', progressListener);

        xhr.open('PUT', part.signedUrl);
        const abortXHR = () => xhr.abort();
        xhr.onreadystatechange = () => {
          if (xhr.readyState === 4 && xhr.status === 200) {
            const ETag = xhr.getResponseHeader('ETag');

            if (ETag) {
              const uploadedPart = {
                partNumber: part.partNumber,
                eTag: ETag.replaceAll('"', ''),
              };

              this.uploadedParts.push(uploadedPart);

              resolve(xhr.status);
              delete this.activeConnections[part.partNumber - 1];
              window.removeEventListener('offline', abortXHR);
            }
          }
        };

        xhr.onerror = error => {
          throwXHRError(error, part, abortXHR);
        };
        xhr.ontimeout = error => {
          throwXHRError(error, part, abortXHR);
        };
        xhr.onabort = () => {
          throwXHRError(new Error('Upload canceled by user or system'), part);
        };
        window.addEventListener('offline', abortXHR);
        xhr.send(file);
      }
    });
  }

  handleProgress(part: number, event: any) {
    if (this.file) {
      if (
        event.type === 'progress' ||
        event.type === 'error' ||
        event.type === 'abort'
      ) {
        this.progressCache[part] = event.loaded;
      }

      if (event.type === 'uploaded') {
        this.uploadedSize += this.progressCache[part] || 0;
        delete this.progressCache[part];
      }

      const inProgress = Object.keys(this.progressCache)
        .map(Number)
        .reduce((memo, id) => (memo += this.progressCache[id]), 0);

      const sent = Math.min(this.uploadedSize + inProgress, this.file.size);

      const total = this.file.size;

      const percentage = Math.round((sent / total) * 100);

      this.onProgressFn({
        sent: sent,
        total: total,
        percentage: percentage,
      });
    }
  }

  async complete(error?: any) {
    if (error && !this.aborted) {
      this.onErrorFn(error);
      return;
    }

    if (error) {
      this.onErrorFn(error);
      return;
    }

    try {
      await this.sendCompleteRequest();
    } catch (error) {
      this.onErrorFn(error);
    }
  }

  async sendCompleteRequest() {
    if (this.fileId) {
      const data = {
        uploadId: this.fileId,
        filePath: this.filePath,
        fileName: this.file?.name,
        chunks: this.uploadedParts.sort((a, b) => a.partNumber - b.partNumber),
      };

      const response = await axiosInt.request({
        url: this.finalizeUploadURL,
        method: 'POST',
        data: data,
      });

      this.onCompleteFn(response.data);
      this.restartUploader();
    }
  }

  onError(onError: (error: any) => void) {
    this.onErrorFn = onError;
    return this;
  }

  onProgress(onProgress: (data: IOnProgressOptions) => void) {
    this.onProgressFn = onProgress;
    return this;
  }

  onComplete(onComplete: (data: IFile) => void) {
    this.onCompleteFn = onComplete;
    return this;
  }

  restartUploader() {
    this.file = undefined;
    this.uploadedSize = 0;
    this.progressCache = {};
    this.activeConnections = {};
    this.parts = [];
    this.uploadedParts = [];
    this.fileId = null;
    this.filePath = null;
    this.numberOfChunkToBeSend = undefined;
  }

  stop() {
    this.abort();
    if (this.file) {
      // TODO: free resources on S3
      this.restartUploader();
    }
  }

  retry() {
    this.sendNext();
  }

  abort() {
    Object.keys(this.activeConnections)
      .map(Number)
      .forEach(id => {
        this.activeConnections[id].abort();
      });

    this.aborted = true;
  }
}

export default ChunkUploader;
